import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
  ChargableEventSetSelector,
  ExternalAssetSelector,
  LayoutSelector,
  TotalPageCountSelector,
} from '../../Selectors';
import {PhotoBookPricingEngine} from '@taopix/taopix-core/dist/types/PricingEngine/PhotoBookPricingEngine';
import {AIMode, CurrencyAdapter, ExternalAssetAdapter} from '@taopix/taopix-core';
import {useTaopix} from '../../Components/Context/TaopixContext';
import useSettings from '../useSettings';
import {useExperience} from '../../Components/Experience/ExperienceProvider';

const useProjectPriceObserver = (): string => {
  const taopix = useTaopix();
  const settings = useSettings();
  const {getAIMode} = useExperience();

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);
  const externalAssets = useSelector(ExternalAssetSelector);
  const calendarEvents: any = useSelector<any>(state => state.project?.calendar?.events) || {};
  const calendarEventSets: any = useSelector<any>(ChargableEventSetSelector) || {};
  const aiMode = getAIMode();

  const customDays = Object.keys(calendarEvents).length;
  const eventSetsCount = Object.keys(calendarEventSets).length;

  const getPricingEngine = (): PhotoBookPricingEngine => {
    const productQuantity = settings.state.price.pricing.quantity || 0;

    // Create a pricing engine object based on the parameters passed in.
    return new (taopix.core.get('pricingEngine'))(
      currency,
      productQuantity,
      settings.state.price.pricing,
      aiMode === AIMode.On
    );
  };

  return useMemo(() => {
    if (!taopix.state.loaded) return '0.00';
    if (!productLayout) return '0.00';
    if (!settings.state.price) return '0.00';

    const projectPrice = getPricingEngine().calculateProjectPrice(
      pageCount,
      ExternalAssetAdapter(externalAssets, productLayout),
      customDays,
      eventSetsCount
    );
    return taopix.core.get('pricingEngine').formatCurrencyNumber(projectPrice, currency);
  }, [
    taopix.state.loaded,
    productLayout?.code,
    productLayout?.usagepicture,
    pageCount,
    aiMode,
    settings.state.price,
    settings.state.price.pricing.quantity,
    settings.state.price.pricing.components,
    customDays,
    eventSetsCount,
    currency,
    externalAssets.length,
  ]);
};

export default useProjectPriceObserver;
